import { CLOUD_TRANSCODE_SUPPORTED_FORMATS } from '../config/dataConfig/FJCloudTranscodingData';
import FJConstant from '../constants/FJConstant';

/**
 * 云转码工具类
 * 提供文件格式检测、任务管理等功能
 */
class FJCloudTranscodingUtil {
    /**
     * 检测文件的媒体类型
     * @param {File|string} fileInput - File对象或文件URL
     * @returns {string|null} 媒体类型 ('video', 'audio', 'image') 或 null
     */
    static detectMediaType(fileInput) {
        try {
            let fileExtension;

            if (fileInput instanceof File) {
                fileExtension = this.getFileExtensionFromFile(fileInput);
            } else if (typeof fileInput === 'string') {
                fileExtension = this._getFileExtension(fileInput);
            } else {
                return null;
            }

            if (!fileExtension) {
                return null;
            }

            // 检查每种媒体类型
            for (const mediaType of ['video', 'audio', 'image']) {
                const supportedFormats = CLOUD_TRANSCODE_SUPPORTED_FORMATS[mediaType];
                if (supportedFormats && supportedFormats.input.includes(fileExtension.toLowerCase())) {
                    return mediaType;
                }
            }

            return null;
        } catch (error) {
            console.error('Error detecting media type:', error);
            return null;
        }
    }

    /**
     * 检测文件格式是否支持
     * @param {File|string} fileInput - File对象或文件URL
     * @param {string} mediaType - 媒体类型 ('video', 'audio', 'image')
     * @returns {boolean} 是否支持
     */
    static checkFormatSupport(fileInput, mediaType = 'video') {
        try {
            let fileExtension;

            if (fileInput instanceof File) {
                // 从File对象获取扩展名
                fileExtension = this.getFileExtensionFromFile(fileInput);
            } else if (typeof fileInput === 'string') {
                // 从S3 URL获取扩展名
                fileExtension = this._getFileExtension(fileInput);
            } else {
                console.warn('Invalid file input type:', typeof fileInput);
                return false;
            }

            const supportedFormats = CLOUD_TRANSCODE_SUPPORTED_FORMATS[mediaType];

            if (!supportedFormats) {
                console.warn(`Unsupported media type: ${mediaType}`);
                return false;
            }

            return supportedFormats.input.includes(fileExtension.toLowerCase());
        } catch (error) {
            console.error('Error checking format support:', error);
            return false;
        }
    }

    /**
     * 创建任务数据对象
     * @param {string} inputS3Url - 输入文件S3链接
     * @param {Object} options - 转码选项
     * @returns {Object} 任务数据对象
     */
    static createTaskData(inputS3Url, options = {}) {
        return {
            inputUrl: inputS3Url,
            status: FJConstant.RUNNING,
            createdAt: new Date().toISOString(),
            progress: 0,
            ...options,
        };
    }

    /**
     * 从File对象获取文件扩展名
     * @param {File} file - File对象
     * @returns {string} 文件扩展名
     * @private
     */
    static getFileExtensionFromFile(file) {
        try {
            if (!file || !(file instanceof File)) {
                return '';
            }

            const fileName = file.name;
            const lastDotIndex = fileName.lastIndexOf('.');

            if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
                return '';
            }

            return fileName.substring(lastDotIndex + 1).toLowerCase();
        } catch (error) {
            console.error('Error getting file extension from File:', error);
            return '';
        }
    }

    /**
     * 根据文件类型和大小来模拟进度递增，但不会超过90%
     * @param {string} mediaType - 媒体类型 ('video', 'audio', 'image')
     * @param {number} size - 文件大小（字节）
     * @param {number} progress - 当前进度
     * @returns {number} 新的进度值
     */
    static simulateProgress(mediaType, size, progress) {
        // 基础进度增量
        let increment = 10;

        // 根据文件类型调整增量
        switch (mediaType) {
            case 'video':
                // 视频文件处理较慢，增量较小
                increment = 5;
                break;
            case 'audio':
                // 音频文件处理中等，增量中等
                increment = 10;
                break;
            case 'image':
                // 图片文件处理较快，增量较大
                increment = 20;
                break;
            default:
                increment = 10;
                break;
        }

        // 根据文件大小调整增量
        if (size) {
            if (size > 10 * 1024 * 1024) {
                // 大于10MB
                increment = Math.max(2, increment - 3);
            } else if (size > 5 * 1024 * 1024) {
                // 大于5MB
                increment = Math.max(3, increment - 2);
            } else if (size > 1 * 1024 * 1024) {
                // 大于1MB
                increment = Math.max(5, increment - 1);
            }
        }

        // 计算新进度
        let newProgress = progress + increment;

        // 确保不超过90%
        if (newProgress > 90) {
            newProgress = 90;
        }

        // 确保不小于当前进度
        if (newProgress < progress) {
            newProgress = progress;
        }

        return newProgress;
    }

    // ========== 私有方法 ==========

    /**
     * 获取文件扩展名（私有方法）
     * @param {string} url - 文件URL
     * @returns {string} 文件扩展名
     * @private
     */
    static _getFileExtension(url) {
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            const lastDotIndex = pathname.lastIndexOf('.');

            if (lastDotIndex === -1) {
                return '';
            }

            return pathname.substring(lastDotIndex + 1).split('?')[0];
        } catch (error) {
            console.error('Error extracting file extension:', error);
            return '';
        }
    }
}

// 导出工具类实例
export default FJCloudTranscodingUtil;
